import type {
  AutoTransactionRuleDto,
  ConditionDto,
  EditActionDto,
} from "../../../api/types";
import { matchesAssignedState } from "../../../components/AssignedStateToggle";
import type { ActionFilter, Filters, RuleSort } from "./types";

/**
 * The searchable text of a condition. A `ref` contributes the *name* of the set
 * it cites, not its contents — searching "unclassified" should find the rules
 * citing that set, and its id would match nothing a person would type. Callers
 * pass `setName` to resolve it; without one, refs simply contribute nothing.
 */
const conditionHaystack = (
  c: ConditionDto,
  setName?: (id: string) => string | undefined,
): string => {
  if (c.type === "field") return c.value.toLowerCase();
  if (c.type === "ref") return setName?.(c.set)?.toLowerCase() ?? "";
  if (c.type === "retired") return "";
  return c.conditions.map((sub) => conditionHaystack(sub, setName)).join(" ");
};

/** The same, for the action side's `applyActionSet`. */
const actionHaystack = (
  actions: EditActionDto[],
  setName?: (id: string) => string | undefined,
): string =>
  actions
    .map((a) => {
      if (a.field === "applyActionSet") {
        // The values too: searching "Netflix" should find the rule that fills
        // a shared subscription set in with it.
        const name = setName?.(a.value.set) ?? "";
        const values = a.value.inputs.map((i) => i.value).join(" ");
        return `${name} ${values}`.toLowerCase();
      }
      if (a.field === "conditional")
        return actionHaystack([a.value.action], setName);
      return "";
    })
    .join(" ");

const actionMatchesFilter = (
  a: EditActionDto,
  filter: Exclude<ActionFilter, "any">,
): boolean => {
  switch (filter) {
    case "category":
      return a.field === "addCategory" || a.field === "removeCategory";
    case "attachment":
      return a.field === "addAttachment" || a.field === "removeAttachment";
    case "askUser":
      return a.field === "prompt";
    default:
      return a.field === filter;
  }
};

/** Recurses into `conditional` actions so the filter sees nested touches. */
const ruleHasActionTouching = (
  rule: AutoTransactionRuleDto,
  filter: Exclude<ActionFilter, "any">,
): boolean => {
  const walk = (a: EditActionDto): boolean => {
    if (actionMatchesFilter(a, filter)) return true;
    if (a.field === "conditional") return walk(a.value.action);
    return false;
  };
  return rule.actions.some(walk);
};

export const filterRules = (
  rules: AutoTransactionRuleDto[],
  filters: Filters,
  setNames?: {
    matchSet?: (id: string) => string | undefined;
    actionSet?: (id: string) => string | undefined;
  },
): AutoTransactionRuleDto[] => {
  const needle = filters.namePattern.trim().toLowerCase();

  return rules.filter((rule) => {
    if (!matchesAssignedState(rule.enabled, filters.enabled)) return false;

    if (
      filters.actionField !== "any" &&
      !ruleHasActionTouching(rule, filters.actionField)
    )
      return false;

    if (needle) {
      const haystack = [
        rule.name.toLowerCase(),
        rule.description?.toLowerCase() ?? "",
        rule.id.toLowerCase(),
        conditionHaystack(rule.condition, setNames?.matchSet),
        actionHaystack(rule.actions, setNames?.actionSet),
      ].join(" ");
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
};

export const sortRules = (
  rules: AutoTransactionRuleDto[],
  sort: RuleSort,
): AutoTransactionRuleDto[] => {
  const out = [...rules];
  switch (sort) {
    case "priorityDesc":
      out.sort(
        (a, b) => b.priority - a.priority || a.name.localeCompare(b.name),
      );
      break;
    case "priorityAsc":
      out.sort(
        (a, b) => a.priority - b.priority || a.name.localeCompare(b.name),
      );
      break;
    case "nameAsc":
      out.sort((a, b) => a.name.localeCompare(b.name));
      break;
  }
  return out;
};
